import axios from 'axios';
import { RATES_API } from './config';
import { Subject } from 'rxjs';
import { adaptPayment, adaptPayments } from './adapters';
import { isTokenExpired, clearAuthData } from '../utils/auth';
import { refreshToken as backendRefreshToken } from '../utils/refreshToken';
import { API_CONFIG } from './config';

export const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: API_CONFIG.HEADERS
});
// Simple pub-sub for global error messages consumed by UI (GlobalSnackbar)
export const apiError$ = new Subject<string>();

// Simple helper to fetch USD->ZiG rate
export async function fetchUsdToZigRate(): Promise<number> {
  try {
    const res = await api.get(RATES_API.USD_TO_ZIG);
    const rate = Number(res.data?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  } catch (_e) {
    return 1; // Fallback to 1 if unavailable
  }
}

// Helper: read access token from common storage keys (Keycloak or custom)
function getStoredAccessToken(): string | null {
  const candidates = [
    localStorage.getItem('access_token'),
    localStorage.getItem('kc_access_token'),
    localStorage.getItem('keycloak_token'),
    localStorage.getItem('token'),
    sessionStorage.getItem('access_token'),
    sessionStorage.getItem('kc_access_token')
  ];
  return candidates.find((v) => typeof v === 'string' && v.length > 0) || null;
}

// Add response interceptor for handling 401/403 errors and logging responses
api.interceptors.response.use(
  (response) => {
    // Enhanced logging for payments responses
    const isPaymentsCall = (response.config.url || '').startsWith('/api/v1/payments') || (response.config.url || '').startsWith('/api/payments');
    if (isPaymentsCall) {
      // eslint-disable-next-line no-console
      console.log('✅ [INCOMING RESPONSE]');
      // eslint-disable-next-line no-console
      console.log(JSON.stringify({
        method: response.config.method?.toUpperCase(),
        url: response.config.baseURL + (response.config.url || ''),
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        data: response.data
      }, null, 2));
    }
    
    // Log the response in a readable JSON format
    const logObj = {
      url: response.config.baseURL + (response.config.url || ''),
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      headers: response.headers
    };
    return response;
  },
  (error) => {
    // Enhanced error logging for payments
    const isPaymentsCall = (error.config?.url || '').startsWith('/api/v1/payments') || (error.config?.url || '').startsWith('/api/payments');
    if (isPaymentsCall) {
      // eslint-disable-next-line no-console
      console.error('❌ [PAYMENTS ERROR]');
      // eslint-disable-next-line no-console
      console.error(JSON.stringify({
        method: error.config?.method?.toUpperCase(),
        url: error.config?.baseURL + (error.config?.url || ''),
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data,
        message: error.message
      }, null, 2));
    }
    
    // Never redirect or clear auth on 403 - surface the error to the UI
    if (error.response?.status === 403) {
      // Optional: lightweight console for quicker diagnosis
      try {
        const logObj = {
          url: error.response.config.baseURL + (error.response.config.url || ''),
          status: error.response.status,
          data: error.response.data,
        };
        // eslint-disable-next-line no-console
        console.warn('[API 403 FORBIDDEN]', logObj);
      } catch {}
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Attempt backend-driven token refresh once, but do NOT redirect on failure
      const currentRefreshToken = localStorage.getItem('refresh_token');
      if (currentRefreshToken) {
        const originalRequest: any = error.config || {};
        if (originalRequest._retry) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        return backendRefreshToken(currentRefreshToken)
          .then((data: any) => {
            const newAccess = data.access_token || data.accessToken || data.token;
            const newRefresh = data.refresh_token || data.refreshToken;
            if (newAccess) {
              localStorage.setItem('access_token', newAccess);
              api.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
            }
            if (newRefresh) {
              localStorage.setItem('refresh_token', newRefresh);
            }
            originalRequest.headers = originalRequest.headers || {};
            if (newAccess) {
              originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
            }
            return api.request(originalRequest);
          })
          .catch(() => {
            // Surface 401 to UI; do not clear or redirect automatically
            return Promise.reject(error);
          });
      }
      // No refresh token available; surface error without redirect
      return Promise.reject(error);
    }
    // Log error responses in a readable JSON format
    if (error.response) {
      const logObj = {
        url: error.response.config.baseURL + (error.response.config.url || ''),
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      };
      console.error('[API ERROR RESPONSE]\n' + JSON.stringify(logObj, null, 2));
    } else {
      console.error('[API ERROR]', error);
    }
    // Push a friendly message to global snackbar
    try {
      const status = error.response?.status;
      const detail = error.response?.data?.message || error.message || 'Request failed';
      const path = error.config?.url || '';
      apiError$.next(`${status || ''} ${path}: ${detail}`.trim());
    } catch {}
    return Promise.reject(error);
  }
);

// Add request interceptor to add token if it exists
api.interceptors.request.use(
  (config) => {
    // Normalize duplicate /api when baseURL ends with /api and path starts with /api/
    try {
      const currentBase = (config.baseURL || '').replace(/\/$/, '');
      const currentUrl = config.url || '';
      if (currentBase.endsWith('/api') && currentUrl.startsWith('/api/')) {
        config.url = currentUrl.replace(/^\/api/, '');
      }
    } catch {}

    // Skip token check for authentication endpoints (login, register, etc.)
    const isAuthEndpoint = config.url?.includes('/auth/') || 
                          config.url?.includes('/login') || 
                          config.url?.includes('/register') ||
                          config.url?.includes('/refresh');
    
    // Also skip if we're currently on the login page
    const isOnLoginPage = window.location.pathname === '/login' || 
                         window.location.pathname === '/register';
    
    if (isAuthEndpoint || isOnLoginPage) {

      // Still add token if it exists (for refresh token requests)
      const token = getStoredAccessToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    }

    // If token is expired, try backend refresh before blocking the request
    // Only check expiration for non-auth endpoints to avoid infinite loops
    try {
      // Skip expiration check for auth endpoints to prevent redirect loops
      if (!isAuthEndpoint && isTokenExpired()) {
        const currentRefreshToken = localStorage.getItem('refresh_token');
        if (currentRefreshToken) {
          return backendRefreshToken(currentRefreshToken)
            .then((data: any) => {
              const newAccess = data.access_token || data.accessToken || data.token;
              const newRefresh = data.refresh_token || data.refreshToken;
              if (newAccess) localStorage.setItem('access_token', newAccess);
              if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
              config.headers = config.headers || {};
              config.headers['Authorization'] = `Bearer ${newAccess}`;

              return config;
            })
            .catch((e: any) => {
              // Don't auto-redirect on refresh failure - let the component handle it
              // Only clear auth if we're sure the token is invalid
              console.warn('[AUTH INTERCEPTOR] Token refresh failed, but not redirecting to prevent session issues');
              return Promise.reject(e);
            });
        } else {
          // No refresh token - but don't auto-redirect, let the component decide
          console.warn('[AUTH INTERCEPTOR] No refresh token available');
          return Promise.reject(new Error('No refresh token'));
        }
      }

    } catch (error) {
      // Don't auto-redirect on token check errors - might be temporary
      console.error('[AUTH INTERCEPTOR] Error checking token:', error);
      // Continue with the request - let the backend handle auth
      // return Promise.reject(new Error('Auth check failed'));
    }

    // Add token to request headers
    const token = getStoredAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Comprehensive debug logging for all requests
    try {
      const isPaymentsCall = (config.url || '').startsWith('/api/v1/payments') || (config.url || '').startsWith('/api/payments');
      if (isPaymentsCall) {
        const authHeader = (config.headers || {})['Authorization'] as string | undefined;
        const masked = authHeader?.startsWith('Bearer ')
          ? ('Bearer ' + (authHeader.slice(7, 17) + '...'))
          : (authHeader ? '[present]' : '[missing]');
        
        // eslint-disable-next-line no-console
        console.log('🚀 [OUTGOING REQUEST]');
        // eslint-disable-next-line no-console
        console.log(JSON.stringify({
          method: config.method?.toUpperCase(),
          url: config.baseURL + (config.url || ''),
          headers: {
            ...config.headers,
            Authorization: authHeader ? masked : '[missing]'
          },
          data: config.data ? (typeof config.data === 'string' ? config.data : config.data) : '[no body]',
          params: config.params || '[no params]'
        }, null, 2));
      }
    } catch (error) {
      console.error('Error logging request:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Loan Origination System API (for loan applications, NOT Fineract)
export const trackLoan = async (trackingNumber: string) => {
  const response = await api.get(`/api/v1/loan-origination/track/${trackingNumber}`);
  return response.data;
};

export const getLoanPaymentRequests = async (trackingNumber: string) => {
  const response = await api.get(`/api/v1/loan-origination/payments/tracking/${trackingNumber}`);
  return response.data;
};

export const getUserPaymentRequests = async (userId: string) => {
  const response = await api.get(`/api/v1/loan-origination/user/${userId}/payment-requests`);
  return response.data;
};

// Get all loan applications for a specific client/user
export const getLoansByClientId = async (clientId: string) => {
  const response = await api.get(`/api/v1/loan-origination/client/${clientId}`);
  return response.data;
};

// Get tracked loans for current user
export const getTrackedLoans = async () => {
  const response = await api.get('/api/v1/loan-origination/tracked');
  return response.data;
};

// LMS-backed: get all loan applications for the current user (by userId)
export const getLmsLoansByUserId = async (userId: string) => {
  const response = await api.get(`/api/v1/loan-management/loans/user/${userId}`);
  return response.data;
};

// LMS-backed: client tracking info list for a user (mirrors single tracking response shape per loan)
export const getClientTrackingByUser = async (userId: string) => {
  const response = await api.get(`/api/v1/loans/client-tracking/user/${userId}`);
  return response.data;
};

// New Payment Integration API endpoints
export const createPaymentRequest = async (paymentData: {
  loanId: number;
  trackingNumber: string;
  amount: number;
  paymentMethod: string;
  currency: string;
  description: string;
  clientReference?: string;
  contactNumber?: string;
  email?: string;
}) => {
  const response = await api.post(`/api/v1/loan-tracking/payment-requests/create`, paymentData);
  return response.data;
};

export const getPaymentRequestByTrackingNumber = async (trackingNumber: string) => {
  const response = await api.get(`/api/v1/loan-tracking/payment-requests/tracking/${trackingNumber}`);
  return response.data;
};
// Payment read helpers using adapters (example usages)
export const getPaymentById = async (id: number) => {
  const res = await api.get(`/api/v1/payments/${id}`);
  return adaptPayment(res.data);
};

export const getPaymentsByLoanId = async (loanId: number) => {
  const res = await api.get(`/api/v1/payments/loan/${loanId}`);
  return adaptPayments(res.data);
};


export const confirmPaymentCompletion = async (confirmationData: {
  loanId: number;
  trackingNumber: string;
  paymentReference: string;
  paynowReference: string;
  amount: number;
  status: 'COMPLETED' | 'FAILED';
  paymentDate: string;
  paymentMethod: string;
  currency: string;
  description: string;
  customerEmail: string;
  customerName: string;
  transactionId?: string;
  receiptNumber?: string;
  errorMessage?: string;
  errorCode?: string;
}) => {
  const response = await api.post(`/api/v1/loan-tracking/payments/confirmation`, confirmationData);
  return response.data;
}; 

// Withdraw a client loan application
export const clientWithdrawLoanApplication = async (payload: { trackingNumber: string; userId: string; reason?: string }) => {
  const response = await api.post(`/api/v1/client-loans/client-withdraw`, payload);
  return response.data;
};

export const getForeclosureBalance = async (loanId: number) => {
  const response = await api.get(`/api/fineract-loans/${loanId}/foreclosure-balance`);
  return response.data;
};

